/**
 * OsLink - OS-aware Link component
 *
 * A drop-in replacement for React Router's Link that automatically handles
 * OS mode navigation. In classic mode, it behaves like a normal Link.
 * In OS mode, it opens the target in a new window instead of navigating.
 */

import React, { useCallback, forwardRef } from "react";
import { Link, useNavigate, type LinkProps } from "react-router";
import { useIsOsMode } from "./OsContext";
import { useWindowId } from "./OsWindow";
import { normalizeOsNavigateTarget, parseUrlToWindowConfig } from "./urlMapping";
import { useOpenUrl } from "./openUrl";
import { preloadOsApp } from "./osAppPrefetch";
import { useOpenInNewWindowMenu } from "./OsOpenInNewWindowMenu";

/** OS window navigation when in aniOS layout or inside an OsWindow (WindowContext). */
function useOsNavigationActive(): boolean {
  const isOsMode = useIsOsMode();
  const windowId = useWindowId();
  return isOsMode || Boolean(windowId);
}

function prefetchOsWindowForPath(to: string): void {
  const target = normalizeOsNavigateTarget(to);
  const windowConfig = parseUrlToWindowConfig(target);
  if (windowConfig?.appId) {
    void preloadOsApp(windowConfig.appId);
  }
}

export interface OsLinkProps extends Omit<LinkProps, "to"> {
  /** The URL path to navigate to */
  to: string;
  /** Optional custom title for the OS window (defaults to parsed title) */
  windowTitle?: string;
  /** If true, always use standard Link navigation even in OS mode */
  forceClassicNavigation?: boolean;
  children: React.ReactNode;
}

/**
 * OsLink - A Link component that's aware of OS mode.
 *
 * In classic mode: Behaves exactly like React Router's Link
 * In OS mode: Opens the target URL in a new OS window
 *
 * @example
 * // Basic usage - works in both modes automatically
 * <OsLink to="/ops/checklists">Checklists</OsLink>
 *
 * @example
 * // With custom window title
 * <OsLink to="/ops/checklists" windowTitle="Daily Checklists">
 *   Checklists
 * </OsLink>
 *
 * @example
 * // Force classic navigation even in OS mode (e.g., for auth redirects)
 * <OsLink to="/login" forceClassicNavigation>
 *   Login
 * </OsLink>
 */
export const OsLink = forwardRef<HTMLAnchorElement, OsLinkProps>(
  (
    {
      to,
      windowTitle,
      forceClassicNavigation = false,
      onClick,
      children,
      ...linkProps
    },
    ref
  ) => {
    const osNavigationActive = useOsNavigationActive();
    const openUrl = useOpenUrl();
    const navigate = useNavigate();

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);

        if (e.defaultPrevented || forceClassicNavigation || !osNavigationActive) {
          return;
        }

        e.preventDefault();

        const target = normalizeOsNavigateTarget(to);
        const handled = openUrl(target, { title: windowTitle });
        if (!handled) {
          navigate(target);
        }
      },
      [
        onClick,
        forceClassicNavigation,
        osNavigationActive,
        openUrl,
        navigate,
        to,
        windowTitle,
      ]
    );

    const linkTo = normalizeOsNavigateTarget(to);
    const {
      onMouseEnter: onMouseEnterProp,
      onPointerDown: onPointerDownProp,
      onContextMenu: onContextMenuProp,
      ...restLinkProps
    } = linkProps;
    const newWindowMenu = useOpenInNewWindowMenu({ enabled: !forceClassicNavigation });
    const prefetchTarget = () => {
      if (!osNavigationActive || forceClassicNavigation) {
        return;
      }
      prefetchOsWindowForPath(to);
    };

    return (
      <>
        <Link
          {...restLinkProps}
          ref={ref}
          to={linkTo}
          onClick={handleClick}
          onMouseEnter={(event) => {
            onMouseEnterProp?.(event);
            prefetchTarget();
          }}
          onPointerDown={(event) => {
            onPointerDownProp?.(event);
            prefetchTarget();
          }}
          onContextMenu={(event) => {
            onContextMenuProp?.(event);
            newWindowMenu.onContextMenu(event, to, windowTitle);
          }}
        >
          {children}
        </Link>
        {newWindowMenu.menu}
      </>
    );
  }
);

OsLink.displayName = "OsLink";

/**
 * Hook to get an OS-aware navigate function.
 *
 * In classic mode: Returns a function that calls React Router's navigate
 * In OS mode: Returns a function that opens the target in a new OS window
 *
 * @returns A navigate function that works in both modes
 *
 * @example
 * function MyComponent() {
 *   const osNavigate = useOsNavigate();
 *
 *   const handleClick = () => {
 *     osNavigate("/ops/checklists", "Checklists");
 *   };
 *
 *   return <button onClick={handleClick}>Open Checklists</button>;
 * }
 */
export function useOsNavigate(): (
  to: string,
  windowTitle?: string,
  forceClassicNavigation?: boolean
) => void {
  const osNavigationActive = useOsNavigationActive();
  const openUrl = useOpenUrl();
  const navigate = useNavigate();

  return useCallback(
    (to: string, windowTitle?: string, forceClassicNavigation = false) => {
      const target = normalizeOsNavigateTarget(to);

      if (forceClassicNavigation || !osNavigationActive) {
        navigate(target);
        return;
      }

      const handled = openUrl(target, { title: windowTitle });
      if (!handled) {
        navigate(target);
      }
    },
    [osNavigationActive, openUrl, navigate]
  );
}

export default OsLink;
